<template>
  <div class="flex flex-col flex-1 h-full overflow-hidden bg-n-surface-1">
    <!-- Header - Padrão Chatwoot com max-w-[60rem] -->
    <header class="sticky top-0 z-10 border-b border-n-weak bg-n-surface-1 flex-shrink-0">
      <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full py-4 sm:py-6 px-4 sm:px-6 gap-3 sm:gap-2">
        <!-- Left: ComboBox Customizado -->
        <div class="w-full sm:w-auto flex-shrink-0">
          <PipelineComboBox
            :model-value="selectedPipelineId"
            :options="pipelineOptions"
            :show-create-button="isAdmin"
            :placeholder="$t('DEALS.FORM.PIPELINE.PLACEHOLDER')"
            class="w-full sm:min-w-[250px] sm:max-w-[300px]"
            @update:model-value="onPipelineSelect"
            @create-pipeline="openCreatePipelineChoice"
          />
        </div>

        <!-- Right: Botoes -->
        <div class="relative flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
          <div class="relative">
            <WuiButton
              ref="statusButton"
              variant="ghost"
              color="slate"
              size="sm"
              :title="$t('PIPELINES.KANBAN.BTN_TITLE_STATUS')"
              @click="toggleStatusMenu"
            >
              <span class="i-lucide-tag size-4" :class="isStatusFiltered ? 'text-woot-500' : ''" />
              <span class="hidden sm:inline text-xs ml-1">{{ $t('PIPELINES.KANBAN.BTN_TITLE_STATUS') }}</span>
            </WuiButton>

            <div
              v-if="showStatusMenu"
              ref="statusMenu"
              class="absolute left-0 sm:left-auto sm:right-0 top-11 z-40 w-[200px] max-w-[calc(100vw-1rem)] rounded-lg border border-n-weak bg-n-solid-1 shadow-lg p-3 space-y-2"
            >
              <p class="text-sm font-medium text-n-slate-12">{{ $t('PIPELINES.KANBAN.STATUS_MENU_TITLE') }}</p>
              <div class="space-y-1">
                <label
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  class="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-n-alpha-1 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    class="accent-woot-500 size-3.5 cursor-pointer"
                    :checked="statusFilterMap[opt.value]"
                    @change="toggleStatus(opt.value)"
                  />
                  <span :class="['i-lucide-' + opt.icon, 'size-3.5', opt.color]" />
                  <span class="text-sm text-n-slate-12 select-none">{{ opt.label }}</span>
                </label>
              </div>
            </div>
          </div>
          <div class="relative">
            <WuiButton
              ref="searchButton"
              variant="ghost"
              color="slate"
              icon="i-lucide-search"
              size="sm"
              :title="$t('PIPELINES.KANBAN.BTN_TITLE_SEARCH')"
              @click="handleSearchClick"
            />
            <div
              v-if="showSearchInput"
              ref="searchBox"
              class="absolute right-full mr-2 top-1/2 -translate-y-1/2 z-40 w-[210px] max-w-[54vw]"
            >
              <WuiInput
                ref="searchInput"
                v-model="searchQuery"
                type="search"
                :placeholder="$t('PIPELINES.KANBAN.SEARCH_PLACEHOLDER')"
                class="w-full min-w-0"
                :custom-input-class="[
                  'h-7 text-sm !py-0 ltr:!pl-2 rtl:!pr-2 bg-n-solid-1 rounded-md [&:not(.focus)]:!border-n-weak/80',
                ]"
              />
            </div>
          </div>
          <WuiButton
            id="toggleDealsFilterButton"
            ref="filterButton"
            variant="ghost"
            color="slate"
            icon="i-lucide-filter"
            size="sm"
            :title="$t('PIPELINES.KANBAN.BTN_TITLE_FILTERS')"
            @click="toggleFilterMenu"
          />
          <WuiButton
            ref="sortButton"
            variant="ghost"
            color="slate"
            icon="i-lucide-arrow-up-down"
            size="sm"
            :title="$t('PIPELINES.KANBAN.BTN_TITLE_SORT')"
            @click="toggleSortMenu"
          />
          <!-- Configuracoes - so admin -->
          <WuiButton
            v-if="isAdmin"
            variant="ghost"
            color="slate"
            icon="i-lucide-settings"
            size="sm"
            :disabled="!selectedPipelineId"
            @click="openPipelineSettings"
          />
          <!-- Adicionar Negocio -->
          <WuiButton
            variant="solid"
            color="blue"
            icon="i-lucide-plus"
            size="sm"
            class="flex-1 sm:flex-initial"
            @click="openNewDealModal"
          >
            <span class="hidden sm:inline">{{ $t('PIPELINES.KANBAN.ADD_DEAL_LONG') }}</span>
            <span class="sm:hidden">{{ $t('PIPELINES.KANBAN.ADD_DEAL_SHORT') }}</span>
          </WuiButton>

          <div
            v-if="showFilterMenu"
            ref="filterMenu"
            class="absolute right-0 top-11 z-40 max-w-[calc(100vw-1rem)]"
          >
            <DealsFilter
              v-model="dealFilters"
              @apply-filter="onApplyDealFilter"
              @clear-filters="clearFilters"
              @close="showFilterMenu = false"
            />
          </div>

          <div
            v-if="showSortMenu"
            ref="sortMenu"
            class="absolute right-0 top-11 z-40 w-[260px] max-w-[calc(100vw-1rem)] rounded-lg border border-n-weak bg-n-solid-1 shadow-lg p-3 space-y-3"
          >
            <p class="text-sm font-medium text-n-slate-12">{{ $t('PIPELINES.KANBAN.SORT_TITLE') }}</p>
            <div class="space-y-1">
              <label class="text-xs text-n-slate-11">{{ $t('PIPELINES.KANBAN.SORT_FIELD_LABEL') }}</label>
              <ComboBox
                v-model="sort.field"
                :options="sortFieldOptions"
                class="[&>div>button]:h-9 [&>div>button]:!bg-n-solid-1"
              />
            </div>
            <div class="space-y-1">
              <label class="text-xs text-n-slate-11">{{ $t('PIPELINES.KANBAN.SORT_DIRECTION_LABEL') }}</label>
              <ComboBox
                v-model="sort.direction"
                :options="sortDirectionOptions"
                class="[&>div>button]:h-9 [&>div>button]:!bg-n-solid-1"
              />
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Loading -->
    <div v-if="isLoading" class="flex flex-col items-center justify-center flex-1 gap-4">
      <spinner />
      <p class="text-sm text-n-slate-11">{{ $t('PIPELINES.KANBAN.LOADING') }}</p>
    </div>

    <!-- Empty State: Sem Pipelines -->
    <div v-else-if="allPipelines.length === 0" class="flex items-center justify-center flex-1 px-4">
      <div class="flex flex-col items-center gap-4 text-center">
        <span class="i-lucide-kanban text-5xl text-n-slate-9" />
        <template v-if="isAdmin">
          <h3 class="text-lg font-bold text-n-slate-12">
            {{ $t('PIPELINES.KANBAN.NO_PIPELINES_ADMIN_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 max-w-md">
            {{ $t('PIPELINES.KANBAN.NO_PIPELINES_ADMIN_DESC') }}
          </p>
          <WuiButton
            variant="solid"
            icon="i-lucide-plus"
            :disabled="isPipelineLimitReached"
            :title="isPipelineLimitReached ? 'Limite de pipelines do plano atingido. Faça upgrade para criar mais.' : ''"
            @click="openCreatePipelineChoice"
          >
            {{ $t('PIPELINES.KANBAN.CREATE_PIPELINE_BTN') }}
          </WuiButton>
        </template>
        <template v-else>
          <h3 class="text-lg font-bold text-n-slate-12">
            {{ $t('PIPELINES.KANBAN.NO_PIPELINES_AGENT_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 max-w-md">
            {{ $t('PIPELINES.KANBAN.NO_PIPELINES_AGENT_DESC') }}
          </p>
        </template>
      </div>
    </div>

    <!-- Kanban Board -->
    <div v-else class="flex-1 flex flex-col overflow-hidden">
      <!-- Empty State: Sem Estagios -->
      <div v-if="stages.length === 0" class="flex items-center justify-center flex-1">
        <div class="flex flex-col items-center gap-4 text-center px-4 max-w-md">
          <span class="i-lucide-target text-4xl text-n-slate-9" />
          <h3 class="text-lg font-bold text-n-slate-12">
            {{ noStagesTitle }}
          </h3>
          <p class="text-sm text-n-slate-11">
            <template v-if="isAdmin">
              {{ $t('PIPELINES.KANBAN.NO_STAGES_ADMIN_DESC_PREFIX') }}
              <strong>{{ currentPipeline?.name }}</strong>
              {{ $t('PIPELINES.KANBAN.NO_STAGES_ADMIN_DESC_SUFFIX') }}
            </template>
            <template v-else>
              {{ $t('PIPELINES.KANBAN.NO_STAGES_AGENT_DESC_PREFIX') }} <strong>{{ currentPipeline?.name }}</strong>
              {{ $t('PIPELINES.KANBAN.NO_STAGES_AGENT_DESC_SUFFIX') }}
            </template>
          </p>
          <WuiButton
            v-if="isAdmin"
            variant="solid"
            icon="i-lucide-settings"
            @click="openPipelineSettings"
          >
            {{ $t('PIPELINES.KANBAN.CONFIGURE_PIPELINE_BTN') }}
          </WuiButton>
        </div>
      </div>

      <!-- Container com SCROLL HORIZONTAL -->
      <div
        v-else
        class="flex gap-3 sm:gap-4 px-4 sm:px-6 py-4 overflow-x-auto overflow-y-hidden flex-1"
      >
        <pipeline-column
          v-for="stage in stages"
          :key="stage.id"
          :stage="stage"
          :deals="dealsByStage[stage.id] || []"
          @deal-click="openDealModal"
          @deal-move="handleDealMove"
          @open-conversation="openConversationDock"
          @open-contact="openContactDrawer"
        />
      </div>
    </div>

    <!-- Deal Modal -->
    <deal-modal
      v-if="showDealModal"
      :show="showDealModal"
      :deal="selectedDeal"
      :pipeline-id="selectedPipelineId"
      :stages="stages"
      @close="closeDealModal"
      @submit="handleDealSubmit"
      @delete="handleDealDelete"
    />

    <!-- Modal: Escolher Tipo de Criacao -->
    <CreatePipelineChoiceDialog
      ref="createPipelineChoiceRef"
      @create-blank="openCreateBlankPipeline"
      @create-template="handleCreateTemplate"
    />

    <!-- Modal: Criar Pipeline do Zero -->
    <CreateBlankPipelineDialog
      ref="createBlankPipelineRef"
      @create="handleCreatePipeline"
    />

    <!-- Modal: Templates -->
    <PipelineTemplatesDialog
      ref="pipelineTemplatesRef"
      @create-from-template="handleCreateFromTemplate"
    />

    <ConversationDock
      :show="showConversationDock"
      :display-id="dockConversationId || 0"
      :inbox-id="dockConversationInboxId"
      :conversation-href="dockConversationHref"
      @close="closeConversationDock"
    />

    <ContactDrawer
      :show="showContactDrawer"
      :contact-id="drawerContactId"
      :exclude-conversation-id="drawerExcludeConversationId"
      @close="closeContactDrawer"
      @open-conversation="openConversationDock"
    />
  </div>

  <!-- Search Modal (mobile command-style) -->
  <teleport to="body">
    <transition
      enter-active-class="transition-opacity duration-150"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showSearchModal"
        class="fixed inset-0 z-[12000] bg-black/50 flex flex-col"
        @click.self="closeSearchModal"
      >
        <div class="bg-n-solid-1 shadow-xl overflow-hidden rounded-b-2xl">
          <div class="flex items-center gap-3 px-4 py-3 border-b border-n-weak">
            <span class="i-lucide-search size-4 text-n-slate-11 shrink-0" />
            <input
              ref="searchModalInput"
              v-model="searchQuery"
              type="search"
              :placeholder="$t('PIPELINES.KANBAN.SEARCH_PLACEHOLDER')"
              class="flex-1 bg-transparent text-sm text-n-slate-12 outline-none placeholder:text-n-slate-10"
              @keydown.escape="closeSearchModal"
            />
            <button class="p-1 rounded text-n-slate-11" @click="closeSearchModal">
              <span class="i-lucide-x size-4" />
            </button>
          </div>
          <div class="max-h-[60vh] overflow-y-auto">
            <template v-if="searchQuery.trim()">
              <button
                v-for="deal in filteredDeals.slice(0, 30)"
                :key="`search-result-${deal.id}`"
                class="w-full text-left px-4 py-3 hover:bg-n-alpha-2 border-b border-n-weak/40 last:border-0"
                @click="openDealFromSearch(deal)"
              >
                <p class="text-sm font-medium text-n-slate-12 truncate">{{ deal.title || `Negócio #${deal.display_id || deal.id}` }}</p>
                <p class="text-xs text-n-slate-11 truncate mt-0.5">{{ deal.contact?.name }}</p>
              </button>
              <p v-if="!filteredDeals.length" class="px-4 py-8 text-sm text-center text-n-slate-11">
                Nenhum resultado para "{{ searchQuery }}"
              </p>
            </template>
            <p v-else class="px-4 py-8 text-sm text-center text-n-slate-11">
              {{ $t('PIPELINES.KANBAN.SEARCH_PLACEHOLDER') }}
            </p>
          </div>
        </div>
        <div class="flex-1" @click="closeSearchModal" />
      </div>
    </transition>
  </teleport>

  <ConfirmationModal
    ref="confirmDealDeleteModal"
    :title="$t('PIPELINES.KANBAN.DELETE_DEAL_MODAL_TITLE')"
    :description="$t('PIPELINES.KANBAN.DELETE_DEAL_MODAL_DESC')"
    :confirm-label="$t('PIPELINES.KANBAN.DELETE_DEAL_MODAL_CONFIRM')"
    :cancel-label="$t('PIPELINES.KANBAN.DELETE_DEAL_MODAL_CANCEL')"
  />
</template>

<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import PipelineColumn from './PipelineColumn.vue';
import DealModal from './DealModal.vue';
import CreatePipelineChoiceDialog from './CreatePipelineChoiceDialog.vue';
import CreateBlankPipelineDialog from './CreateBlankPipelineDialog.vue';
import PipelineTemplatesDialog from './PipelineTemplatesDialog.vue';
import PipelineComboBox from './PipelineComboBox.vue';
import DealsFilter from './DealsFilter.vue';
import Spinner from 'shared/components/Spinner.vue';
import WuiButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import WuiInput from 'dashboard/components-next/input/Input.vue';
import ConversationDock from './ConversationDock.vue';
import ContactDrawer from './ContactDrawer.vue';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import DealProductsAPI from 'dashboard/api/pipelines/dealProducts';
import { fetchContactsListWithCache } from 'dashboard/helper/contactDetailsCache';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';

export default {
  name: 'PipelinesKanban',
  components: {
    PipelineColumn,
    DealModal,
    CreatePipelineChoiceDialog,
    CreateBlankPipelineDialog,
    PipelineTemplatesDialog,
    PipelineComboBox,
    DealsFilter,
    Spinner,
    WuiButton,
    ComboBox,
    WuiInput,
    ConversationDock,
    ContactDrawer,
    ConfirmationModal,
  },
  data() {
    return {
      selectedPipelineId: null,
      isLoading: true,
      isBootstrapping: true,
      showDealModal: false,
      selectedDeal: null,
      showConversationDock: false,
      dockConversationId: null,
      dockConversationInboxId: 0,
      showContactDrawer: false,
      drawerContactId: null,
      drawerExcludeConversationId: null,
      showFilterMenu: false,
      showSortMenu: false,
      showStatusMenu: false,
      showOpenDeals: true,
      showWonDeals: true,
      showLostDeals: true,
      showSearchInput: false,
      showSearchModal: false,
      searchQuery: '',
      dealFilters: [
        {
          attributeKey: 'assignee_id',
          filterOperator: 'equal_to',
          values: [],
          queryOperator: 'and',
        },
      ],
      sort: {
        field: 'created_at',
        direction: 'desc',
      },
    };
  },
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
      currentRole: 'getCurrentRole',
    }),
    isAdmin() {
      return this.currentRole === 'administrator';
    },
    lastSelectedStorageKey() {
      return `pipeline_last_selected_${this.currentAccountId}`;
    },
    isPipelineLimitReached() {
      const isAtLimit = this.$store.getters['subscriptionLimits/isAtLimit'];
      return isAtLimit ? isAtLimit('pipelines') : false;
    },
    allPipelines() {
      return this.$store.getters['pipelines/getPipelines'] || [];
    },
    pipelineOptions() {
      return this.allPipelines.map(p => ({
        value: p.id,
        label: p.name,
      }));
    },
    currentPipeline() {
      if (!this.selectedPipelineId) return null;
      return this.$store.getters['pipelines/getPipeline'](this.selectedPipelineId);
    },
    stages() {
      const stages = this.currentPipeline?.stages || [];
      return [...stages].sort((a, b) => a.position - b.position);
    },
    allDeals() {
      const getter = this.$store.getters['deals/getDealsByContext'];
      if (typeof getter !== 'function') {
        return this.$store.getters['deals/getDeals'] || [];
      }
      return getter({
        accountId: this.currentAccountId,
        pipelineId: this.selectedPipelineId,
      });
    },
    agentsOptions() {
      const agents = this.$store.getters['agents/getAgents'] || [];
      return agents.map(agent => ({ value: String(agent.id), label: agent.name }));
    },
    labelsOptions() {
      const labels = this.$store.getters['labels/getLabels'] || [];
      return labels.map(label => ({ value: label.title, label: label.title }));
    },
    sortFieldOptions() {
      return [
        { value: 'created_at', label: this.$t('PIPELINES.KANBAN.SORT_FIELD_CREATED_AT') },
        { value: 'close_date', label: this.$t('PIPELINES.KANBAN.SORT_FIELD_CLOSE_DATE') },
      ];
    },
    sortDirectionOptions() {
      return [
        { value: 'desc', label: this.$t('PIPELINES.KANBAN.SORT_DIR_DESC') },
        { value: 'asc', label: this.$t('PIPELINES.KANBAN.SORT_DIR_ASC') },
      ];
    },
    statusFilterMap() {
      return { open: this.showOpenDeals, won: this.showWonDeals, lost: this.showLostDeals };
    },
    statusOptions() {
      return [
        { value: 'open',  label: this.$t('PIPELINES.KANBAN.STATUS_OPEN'),  icon: 'circle',       color: 'text-n-slate-9'  },
        { value: 'won',   label: this.$t('PIPELINES.KANBAN.STATUS_WON'),   icon: 'circle-check',  color: 'text-green-600'  },
        { value: 'lost',  label: this.$t('PIPELINES.KANBAN.STATUS_LOST'),  icon: 'circle-x',      color: 'text-red-500'    },
      ];
    },
    isStatusFiltered() {
      return !this.showOpenDeals || !this.showWonDeals || !this.showLostDeals;
    },
    noStagesTitle() {
      if (this.isAdmin) {
        return this.$t('PIPELINES.KANBAN.NO_STAGES_ADMIN_TITLE');
      }
      return this.$t('PIPELINES.KANBAN.NO_STAGES_AGENT_TITLE');
    },
    filteredDeals() {
      const getDealLabels = deal => {
        const source = deal?.labels || deal?.label_list || [];
        if (Array.isArray(source)) return source;
        if (typeof source === 'string') {
          return source.split(',').map(item => item.trim()).filter(Boolean);
        }
        return [];
      };

      const getDealPriority = deal => deal?.priority || deal?.custom_attributes?.priority || 'none';

      const getDealAssignees = deal => {
        const customIds = Array.isArray(deal?.custom_attributes?.responsible_agent_ids)
          ? deal.custom_attributes.responsible_agent_ids.map(id => Number(id))
          : [];
        const nativeId = Number(deal?.assignee?.id || deal?.assignee_id);
        return [...new Set([...customIds, nativeId].filter(id => !Number.isNaN(id)))];
      };

      const result = this.allDeals.filter(deal => {
        if (deal.status === 'open' && !this.showOpenDeals) return false;
        if (deal.status === 'won' && !this.showWonDeals) return false;
        if (deal.status === 'lost' && !this.showLostDeals) return false;
        if (!this.matchesDealFilters(deal, { getDealLabels, getDealPriority, getDealAssignees })) {
          return false;
        }
        if (!this.matchesDealSearch(deal)) return false;

        return true;
      });

      const sortMultiplier = this.sort.direction === 'asc' ? 1 : -1;
      return result.sort((a, b) => {
        const aDate = new Date(a?.[this.sort.field] || 0).getTime();
        const bDate = new Date(b?.[this.sort.field] || 0).getTime();
        return (aDate - bDate) * sortMultiplier;
      });
    },
    dealsByStage() {
      const grouped = {};
      this.stages.forEach(stage => {
        grouped[stage.id] = this.filteredDeals.filter(
          deal => deal.pipeline_stage_id === stage.id
        );
      });
      return grouped;
    },
    allConversations() {
      return this.$store.getters.getAllConversations || [];
    },
    dockConversationHref() {
      if (!this.dockConversationId) return '';
      return frontendURL(
        conversationUrl({
          accountId: this.currentAccountId,
          id: this.dockConversationId,
        })
      );
    },
  },
  async mounted() {
    const _u = this.$store.getters['getCurrentUser'];
    const _aid = this.$store.getters['getCurrentAccountId'];
    const _acc = (_u?.accounts || []).find(a => Number(a.id) === Number(_aid));
    const _perms = _acc?.permissions || [];
    if (!_perms.includes('administrator') && !_perms.includes('pipeline.view')) {
      this.$router.replace({ name: 'home' });
      return;
    }
    try {
      await this.loadAuxiliaryData();
      await this.loadPipelines();
      if (this.allPipelines.length > 0) {
        const saved = Number(localStorage.getItem(this.lastSelectedStorageKey));
        const exists = saved && this.allPipelines.some(p => p.id === saved);
        this.selectedPipelineId = exists ? saved : this.allPipelines[0].id;
        await this.loadPipelineData({ showLoader: true });
      } else {
        this.isLoading = false;
      }
    } finally {
      this.isBootstrapping = false;
    }
    document.addEventListener('mousedown', this.handleGlobalClick);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.handleGlobalClick);
  },
  watch: {
    allPipelines: {
      immediate: true,
      handler(pipelines) {
        if (this.isBootstrapping) return;
        if (!pipelines.length) {
          this.selectedPipelineId = null;
          this.isLoading = false;
          return;
        }

        const hasCurrent = pipelines.some(p => p.id === this.selectedPipelineId);
        if (hasCurrent) return;

        this.selectedPipelineId = pipelines[0].id;
        this.loadPipelineData({ showLoader: false });
      },
    },
  },
  methods: {
    extractErrorMessage(error, fallback = 'Erro ao processar a operacao') {
      const payload = error?.response?.data;
      const normalizeKnownMessage = message => {
        if (!message) return message;
        const lower = String(message).toLowerCase();
        if (
          lower.includes('inbox nao permitida neste pipeline') ||
          lower.includes('inbox não permitida neste pipeline')
        ) {
          return 'Esta conversa pertence a uma inbox não permitida neste pipeline. Ajuste as permissões da pipeline ou mova para uma etapa de pipeline permitida.';
        }
        return message;
      };

      const fromErrorsObject = errorsObj => {
        if (!errorsObj || typeof errorsObj !== 'object') return null;
        const messages = Object.values(errorsObj).flatMap(value => {
          if (Array.isArray(value)) return value;
          if (typeof value === 'string') return [value];
          return [];
        });
        return messages.length ? messages.join(', ') : null;
      };

      if (Array.isArray(payload?.errors) && payload.errors.length) {
        return normalizeKnownMessage(payload.errors.join(', '));
      }
      if (payload?.errors && typeof payload.errors === 'object') {
        const parsed = fromErrorsObject(payload.errors);
        if (parsed) return normalizeKnownMessage(parsed);
      }
      if (typeof payload?.error === 'string' && payload.error.trim()) {
        return normalizeKnownMessage(payload.error);
      }
      if (typeof payload?.message === 'string' && payload.message.trim()) {
        return normalizeKnownMessage(payload.message);
      }
      if (typeof error?.message === 'string' && error.message.trim()) {
        return normalizeKnownMessage(error.message);
      }
      return fallback;
    },
    async loadPipelines() {
      try {
        await this.$store.dispatch('pipelines/get', {
          accountId: this.currentAccountId,
        });
      } catch {
        useAlert(this.$t('PIPELINES.TOAST.PIPELINE_LOAD_ERROR'));
      }
    },
    async loadPipelineData({ showLoader = false } = {}) {
      if (showLoader) this.isLoading = true;
      try {
        await this.$store.dispatch('deals/get', {
          accountId: this.currentAccountId,
          pipelineId: this.selectedPipelineId,
        });
      } catch {
        useAlert(this.$t('PIPELINES.TOAST.DEALS_LOAD_ERROR'));
      } finally {
        if (showLoader) this.isLoading = false;
      }
    },
    async loadAuxiliaryData() {
      await Promise.allSettled([
        this.$store.dispatch('agents/get'),
        this.$store.dispatch('labels/get'),
        fetchContactsListWithCache(this.$store, {
          accountId: this.currentAccountId,
        }),
        this.$store.dispatch('attributes/get'),
        this.$store.dispatch('products/get', {
          accountId: this.currentAccountId,
        }),
        this.$store.dispatch('coupons/get', {
          accountId: this.currentAccountId,
        }),
        this.$store.dispatch('dealLossReasons/get', {
          accountId: this.currentAccountId,
        }),
      ]);
    },
    onPipelineSelect(value) {
      this.selectedPipelineId = value;
      localStorage.setItem(this.lastSelectedStorageKey, value);
      this.loadPipelineData({ showLoader: false });
    },
    toggleFilterMenu() {
      this.showFilterMenu = !this.showFilterMenu;
      if (this.showFilterMenu) { this.showSortMenu = false; this.showStatusMenu = false; }
    },
    toggleSortMenu() {
      this.showSortMenu = !this.showSortMenu;
      if (this.showSortMenu) { this.showFilterMenu = false; this.showStatusMenu = false; }
    },
    toggleStatusMenu() {
      this.showStatusMenu = !this.showStatusMenu;
      if (this.showStatusMenu) { this.showFilterMenu = false; this.showSortMenu = false; }
    },
    toggleStatus(value) {
      if (value === 'open') this.showOpenDeals = !this.showOpenDeals;
      if (value === 'won') this.showWonDeals = !this.showWonDeals;
      if (value === 'lost') this.showLostDeals = !this.showLostDeals;
    },
    handleSearchClick() {
      const isMobile = window.innerWidth < 640;
      if (isMobile) {
        this.showSearchModal = true;
        this.$nextTick(() => this.$refs.searchModalInput?.focus());
      } else {
        this.toggleSearchInput();
      }
    },
    closeSearchModal() {
      this.showSearchModal = false;
      this.searchQuery = '';
    },
    openDealFromSearch(deal) {
      this.closeSearchModal();
      this.openDealModal(deal);
    },
    toggleSearchInput() {
      this.showSearchInput = !this.showSearchInput;
      if (this.showSearchInput) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus?.();
        });
      } else {
        this.searchQuery = '';
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.$nextTick(() => {
        this.$refs.searchInput?.focus?.();
      });
    },
    toggleOutcome(status) {
      if (status === 'won') this.showWonDeals = !this.showWonDeals;
      if (status === 'lost') this.showLostDeals = !this.showLostDeals;
    },
    clearFilters() {
      this.dealFilters = [
        {
          attributeKey: 'assignee_id',
          filterOperator: 'equal_to',
          values: [],
          queryOperator: 'and',
        },
      ];
    },
    onApplyDealFilter(payload) {
      this.dealFilters = Array.isArray(payload) ? payload : [];
      this.showFilterMenu = false;
    },
    normalizeFilterValue(value) {
      if (Array.isArray(value)) {
        return value
          .map(item => {
            if (item && typeof item === 'object' && 'id' in item) return String(item.id);
            return String(item);
          })
          .filter(Boolean);
      }
      if (value && typeof value === 'object' && 'id' in value) return String(value.id);
      if (value === null || value === undefined || value === '') return null;
      return String(value);
    },
    matchesDealFilters(deal, helpers) {
      const activeFilters = (this.dealFilters || []).filter(filter => {
        if (!filter?.attributeKey || !filter?.filterOperator) return false;
        if (['is_present', 'is_not_present'].includes(filter.filterOperator)) return true;
        const normalized = this.normalizeFilterValue(filter.values);
        if (Array.isArray(normalized)) return normalized.length > 0;
        return normalized !== null;
      });

      if (!activeFilters.length) return true;

      const getDealValue = filter => {
        switch (filter.attributeKey) {
          case 'assignee_id':
            return helpers.getDealAssignees(deal).map(id => String(id));
          case 'priority':
            return String(helpers.getDealPriority(deal) || 'none');
          case 'labels':
            return helpers.getDealLabels(deal).map(item => String(item));
          case 'created_at':
            return deal.created_at || null;
          case 'close_date':
            return deal.close_date || null;
          default:
            return null;
        }
      };

      const evaluateFilter = filter => {
        const rawValue = getDealValue(filter);
        const normalizedFilterValue = this.normalizeFilterValue(filter.values);
        const isArrayField = Array.isArray(rawValue);
        let normalizedDealValue;
        if (isArrayField) {
          normalizedDealValue = rawValue.map(item => String(item));
        } else if (rawValue === null || rawValue === undefined || rawValue === '') {
          normalizedDealValue = null;
        } else {
          normalizedDealValue = String(rawValue);
        }

        if (filter.filterOperator === 'is_present') {
          return isArrayField ? normalizedDealValue.length > 0 : normalizedDealValue !== null;
        }
        if (filter.filterOperator === 'is_not_present') {
          return isArrayField ? normalizedDealValue.length === 0 : normalizedDealValue === null;
        }

        if (['created_at', 'close_date'].includes(filter.attributeKey)) {
          const dealDate = normalizedDealValue ? new Date(normalizedDealValue) : null;
          if (!dealDate || Number.isNaN(dealDate.getTime())) return false;
          if (filter.filterOperator === 'days_before') {
            const days = Number(normalizedFilterValue);
            if (Number.isNaN(days)) return false;
            const threshold = new Date();
            threshold.setHours(0, 0, 0, 0);
            threshold.setDate(threshold.getDate() - days);
            return dealDate < threshold;
          }
          const filterDate = normalizedFilterValue ? new Date(normalizedFilterValue) : null;
          if (!filterDate || Number.isNaN(filterDate.getTime())) return false;
          if (filter.filterOperator === 'is_greater_than') return dealDate > filterDate;
          if (filter.filterOperator === 'is_less_than') return dealDate < filterDate;
          if (filter.filterOperator === 'equal_to') {
            return dealDate.toISOString().slice(0, 10) === filterDate.toISOString().slice(0, 10);
          }
          if (filter.filterOperator === 'not_equal_to') {
            return dealDate.toISOString().slice(0, 10) !== filterDate.toISOString().slice(0, 10);
          }
          return true;
        }

        let filterValues;
        if (Array.isArray(normalizedFilterValue)) {
          filterValues = normalizedFilterValue;
        } else if (normalizedFilterValue === null) {
          filterValues = [];
        } else {
          filterValues = [normalizedFilterValue];
        }

        const hasIntersection = isArrayField
          ? normalizedDealValue.some(item => filterValues.includes(item))
          : filterValues.includes(normalizedDealValue);

        if (filter.filterOperator === 'equal_to') return hasIntersection;
        if (filter.filterOperator === 'not_equal_to') return !hasIntersection;
        return true;
      };

      let result = evaluateFilter(activeFilters[0]);
      for (let index = 1; index < activeFilters.length; index += 1) {
        const connector = activeFilters[index - 1].queryOperator || 'and';
        const current = evaluateFilter(activeFilters[index]);
        result = connector === 'or' ? result || current : result && current;
      }
      return result;
    },
    matchesDealSearch(deal) {
      const query = String(this.searchQuery || '').trim().toLowerCase();
      if (!query) return true;

      const digitsQuery = query.replace(/\D/g, '');
      const fields = [
        deal?.title,
        deal?.contact?.name,
        deal?.contact?.identifier,
        deal?.contact?.phone_number,
        deal?.contact?.email,
      ].filter(Boolean).map(value => String(value));

      const textMatched = fields.some(value => value.toLowerCase().includes(query));
      if (textMatched) return true;

      if (!digitsQuery) return false;
      return fields.some(value => value.replace(/\D/g, '').includes(digitsQuery));
    },
    handleGlobalClick(event) {
      const filterMenu = this.$refs.filterMenu;
      const sortMenu = this.$refs.sortMenu;
      const statusMenu = this.$refs.statusMenu;
      const searchBox = this.$refs.searchBox;
      const filterButton = this.$refs.filterButton?.$el || this.$refs.filterButton;
      const sortButton = this.$refs.sortButton?.$el || this.$refs.sortButton;
      const statusButton = this.$refs.statusButton?.$el || this.$refs.statusButton;
      const searchButton = this.$refs.searchButton?.$el || this.$refs.searchButton;
      const target = event.target;

      const clickedFilterMenu = filterMenu?.contains?.(target);
      const clickedSortMenu = sortMenu?.contains?.(target);
      const clickedStatusMenu = statusMenu?.contains?.(target);
      const clickedSearchBox = searchBox?.contains?.(target);
      const clickedFilterButton = filterButton?.contains?.(target);
      const clickedSortButton = sortButton?.contains?.(target);
      const clickedStatusButton = statusButton?.contains?.(target);
      const clickedSearchButton = searchButton?.contains?.(target);

      if (!clickedFilterMenu && !clickedFilterButton) {
        this.showFilterMenu = false;
      }
      if (!clickedSortMenu && !clickedSortButton) {
        this.showSortMenu = false;
      }
      if (!clickedStatusMenu && !clickedStatusButton) {
        this.showStatusMenu = false;
      }
      if (!clickedSearchBox && !clickedSearchButton && !this.searchQuery) {
        this.showSearchInput = false;
        this.showSearchModal = false;
      }
    },
    handlePipelineChange() {
      this.loadPipelineData({ showLoader: false });
    },
    openNewDealModal() {
      this.selectedDeal = null;
      this.showDealModal = true;
    },
    openDealModal(deal) {
      this.selectedDeal = deal;
      this.showDealModal = true;
    },
    closeDealModal() {
      this.showDealModal = false;
      this.selectedDeal = null;
    },
    async handleDealSubmit(dealData) {
      const selectedProducts = Array.isArray(dealData.selected_products)
        ? dealData.selected_products
        : [];
      const payload = { ...dealData };
      delete payload.selected_products;

      try {
        let persistedDealId = null;
        if (this.selectedDeal) {
          await this.$store.dispatch('deals/update', {
            accountId: this.currentAccountId,
            dealId: this.selectedDeal.id,
            deal: payload,
          });
          persistedDealId = this.selectedDeal.id;
          useAlert(this.$t('DEALS.EDIT.SUCCESS'));
        } else {
          const created = await this.$store.dispatch('deals/create', {
            accountId: this.currentAccountId,
            deal: payload,
          });
          persistedDealId = created?.id || created?.data?.id || null;
          useAlert(this.$t('DEALS.CREATE.SUCCESS'));
        }

        if (persistedDealId) {
          await this.syncDealProducts({
            dealId: persistedDealId,
            existingProducts: this.selectedDeal?.products || [],
            selectedItems: selectedProducts,
          });
        }
        this.closeDealModal();
      } catch (error) {
        useAlert(this.extractErrorMessage(error, this.$t('PIPELINES.TOAST.DEAL_SAVE_ERROR')));
      }
    },
    async syncDealProducts({ dealId, existingProducts, selectedItems }) {
      const persistedMap = new Map();
      (existingProducts || []).forEach(item => {
        const productId = Number(item.product_id);
        if (Number.isNaN(productId)) return;
        persistedMap.set(productId, item);
      });

      const selectedMap = new Map();
      (selectedItems || []).forEach(item => {
        const productId = Number(item.product_id);
        if (Number.isNaN(productId)) return;
        selectedMap.set(productId, item);
      });

      const operations = [];

      persistedMap.forEach((persisted, productId) => {
        if (selectedMap.has(productId)) return;
        const relationId = Number(persisted.id || persisted.deal_product_id);
        if (!Number.isNaN(relationId) && relationId > 0) {
          operations.push(
            DealProductsAPI.delete(this.currentAccountId, dealId, relationId)
          );
        }
      });

      selectedMap.forEach((selected, productId) => {
        const quantity = Number(selected.quantity || 1);
        const unitPrice = Number(selected.unit_price || 0);
        const existing = persistedMap.get(productId);
        const relationId = Number(
          selected.deal_product_id || existing?.id || existing?.deal_product_id
        );

        if (!Number.isNaN(relationId) && relationId > 0) {
          operations.push(
            DealProductsAPI.update(this.currentAccountId, dealId, relationId, {
              product_id: productId,
              quantity,
              unit_price: unitPrice,
            })
          );
          return;
        }

        operations.push(
          DealProductsAPI.create(this.currentAccountId, dealId, {
            product_id: productId,
            quantity,
            unit_price: unitPrice,
          })
        );
      });

      if (operations.length) {
        await Promise.all(operations);
      }
    },
    async handleDealMove({ dealId, targetStageId }) {
      try {
        await this.$store.dispatch('deals/move', {
          accountId: this.currentAccountId,
          dealId,
          targetStageId,
        });
      } catch (error) {
        useAlert(this.extractErrorMessage(error, this.$t('PIPELINES.TOAST.DEAL_MOVE_ERROR')));
      }
    },
    async handleDealDelete(dealId) {
      if (!dealId) return;
      const confirmed = await this.$refs.confirmDealDeleteModal.showConfirmation();
      if (!confirmed) return;
      try {
        await this.$store.dispatch('deals/delete', {
          accountId: this.currentAccountId,
          dealId,
        });
        this.closeDealModal();
        useAlert(this.$t('PIPELINES.TOAST.DEAL_DELETED'));
      } catch {
        useAlert(this.$t('PIPELINES.TOAST.DEAL_DELETE_ERROR'));
      }
    },
    async openConversationDock(conversationId) {
      const conversationIdNumber = Number(conversationId);
      if (!conversationIdNumber) return;
      let conversation = this.allConversations.find(
        item => Number(item.id) === conversationIdNumber
      );
      if (!conversation) {
        await this.$store.dispatch('getConversation', conversationIdNumber).catch(() => null);
        const getConversationById = this.$store.getters.getConversationById;
        if (typeof getConversationById === 'function') {
          conversation = getConversationById(conversationIdNumber);
        }
        if (!conversation) {
          conversation = this.allConversations.find(
            item => Number(item.display_id) === conversationIdNumber
          );
        }
      }
      if (!conversation) {
        useAlert(this.$t('PIPELINES.TOAST.CONVERSATION_OPEN_ERROR'));
        return;
      }
      await this.$store.dispatch('setActiveChat', { data: conversation }).catch(() => null);
      this.dockConversationId = Number(conversation.display_id || conversationIdNumber);
      this.dockConversationInboxId = Number(conversation.inbox_id || 0);
      this.showConversationDock = true;
    },
    closeConversationDock() {
      this.showConversationDock = false;
      this.dockConversationId = null;
      this.dockConversationInboxId = 0;
    },
    openContactDrawer(payload) {
      const contactId = typeof payload === 'object' ? payload.contactId : payload;
      const excludeConversationId = typeof payload === 'object' ? payload.excludeConversationId : null;
      if (!contactId) return;
      this.drawerContactId = Number(contactId);
      this.drawerExcludeConversationId = excludeConversationId ? Number(excludeConversationId) : null;
      this.showContactDrawer = true;
    },
    closeContactDrawer() {
      this.showContactDrawer = false;
      this.drawerContactId = null;
      this.drawerExcludeConversationId = null;
    },
    // --- Pipeline creation ---
    openCreatePipelineChoice() {
      this.$refs.createPipelineChoiceRef?.open();
    },
    openCreateBlankPipeline() {
      this.$refs.createPipelineChoiceRef?.close();
      this.$refs.createBlankPipelineRef?.open();
    },
    handleCreateTemplate() {
      this.$refs.createPipelineChoiceRef?.close();
      this.$refs.pipelineTemplatesRef?.open();
    },
    async handleCreateFromTemplate(template) {
      try {
        const newPipeline = await this.$store.dispatch('pipelines/createFromTemplate', {
          accountId: this.currentAccountId,
          templateId: template.id,
        });
        const pipeline = newPipeline?.data || newPipeline;
        useAlert(this.$t('PIPELINES.CREATE.API.SUCCESS_MESSAGE'));
        this.selectedPipelineId = pipeline.id;
        this.$refs.pipelineTemplatesRef?.close();
        await this.loadPipelineData();
      } catch {
        useAlert(this.$t('PIPELINES.TOAST.PIPELINE_CREATE_TEMPLATE_ERROR'));
      }
    },
    openPipelineSettings() {
      if (!this.isAdmin) {
        useAlert(this.$t('PIPELINES.TOAST.ADMIN_ONLY_SETTINGS'));
        return;
      }
      if (!this.selectedPipelineId) return;
      this.$router.push(
        `/app/accounts/${this.currentAccountId}/settings/pipelines/${this.selectedPipelineId}`
      );
    },
    async handleCreatePipeline(pipelineData) {
      try {
        const newPipeline = await this.$store.dispatch('pipelines/create', {
          accountId: this.currentAccountId,
          pipeline: pipelineData,
        });
        const pipeline = newPipeline?.data || newPipeline;
        useAlert(this.$t('PIPELINES.CREATE.API.SUCCESS_MESSAGE'));
        this.selectedPipelineId = pipeline.id;
        this.$refs.createBlankPipelineRef?.close();
        this.$refs.createBlankPipelineRef?.stopLoading();
        await this.loadPipelineData();
      } catch {
        useAlert(this.$t('PIPELINES.CREATE.API.ERROR_MESSAGE'));
        this.$refs.createBlankPipelineRef?.stopLoading();
      }
    },
  },
};
</script>

<style scoped>
/* Scrollbar horizontal personalizada */
.overflow-x-auto::-webkit-scrollbar {
  height: 8px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: var(--n-slate-2, #f1f5f9);
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: var(--n-slate-4, #cbd5e1);
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: var(--n-slate-5, #94a3b8);
}

/* Smooth scroll em mobile */
.overflow-x-auto {
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}


</style>
